// AF-3.16 — EmptyState / ErrorState / StaleState.
// AF-3.20.5 — light-theme visual pass + premium empty-state copy pattern
// (what is missing / why it matters / what to do next), per spec Phase 8.
//
// Every API-backed brand screen must use one of these instead of a blank
// screen or a generic "No data" string (spec Sections 22-23). None of
// these components fabricate a value -- they only ever render copy
// supplied by the caller plus, for ErrorState, the actual error detail
// the backend returned.

import { ArrowClockwise, Clock } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

import { GLIMState } from "@/brand";

// Visual System v1: empty / loading / error are expressed by GLIM (the face)
// through the Product State Engine, always alongside real text -- the state is
// never understood through the character alone.
export function EmptyState({ title, description, whyItMatters, actionLabel, onAction, testId = "empty-state", glimState = "neutral" }) {
  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center text-center py-12 px-6 rounded-[var(--radius-card)] border border-dashed border-[var(--border)] bg-[var(--surface-sunken)]">
      <div className="mb-2"><GLIMState state={glimState} size={56} showLabel={false} announce={false} /></div>
      <p className="text-[12px] font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-1">{title}</p>
      {description && <p className="text-[11px] text-[var(--text-secondary)] max-w-sm">{description}</p>}
      {whyItMatters && <p className="text-[10px] text-[var(--text-tertiary)] max-w-sm mt-1.5">{whyItMatters}</p>}
      {actionLabel && onAction && (
        <Button data-testid={`${testId}-action`} variant="link" size="sm" className="mt-3 text-[var(--accent)] text-[11px] font-medium" onClick={onAction}>
          {actionLabel} →
        </Button>
      )}
    </div>
  );
}

// The one mandated, literal empty-state case: an entity Martian has no
// evidence for. Never substitute a generic objective or fabricated demo
// values here (spec Section 22).
export function InsufficientEvidenceState({ entityLabel, testId = "insufficient-evidence-state" }) {
  return (
    <EmptyState
      testId={testId}
      title="INSUFFICIENT EVIDENCE"
      description={`Martian cannot establish a primary objective${entityLabel ? ` for ${entityLabel}` : " for this entity"} yet.`}
      whyItMatters="Recommendations only render once there is real, connected data to reason from — Martian does not estimate to fill the gap."
      actionLabel="Connect a data source"
    />
  );
}

export function ErrorState({ error, onRetry, testId = "error-state" }) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail || error?.message || "An unexpected error occurred.";
  let whatHappened = "Something went wrong loading this data.";
  let whatYouCanDo = "Try again. If this keeps happening, contact support.";
  if (status === 404) { whatHappened = "The requested object was not found."; whatYouCanDo = "It may not exist yet, or you may not have access to it."; }
  else if (status === 403 || status === 401) { whatHappened = "You do not have permission to view this."; whatYouCanDo = "Confirm you are in the correct workspace."; }
  else if (status >= 500) { whatHappened = "Martian's backend returned a server error."; whatYouCanDo = "This is usually temporary."; }

  return (
    <div data-testid={testId} className="flex flex-col items-center justify-center text-center py-12 px-6 rounded-[var(--radius-card)] border border-[var(--error-border)] bg-[var(--error-bg)]">
      <div className="mb-2"><GLIMState state="error" size={56} showLabel={false} announce={false} /></div>
      <p className="text-[12px] font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-2">What happened</p>
      <p className="text-[11px] text-[var(--text-secondary)] max-w-sm mb-2">{whatHappened}</p>
      <p className="text-[10px] font-mono text-[var(--text-tertiary)] max-w-sm mb-3">{detail}</p>
      <p className="text-[12px] font-semibold text-[var(--text-primary)] uppercase tracking-wide mb-1">What you can do</p>
      <p className="text-[11px] text-[var(--text-secondary)] max-w-sm mb-3">{whatYouCanDo}</p>
      {onRetry && (
        <Button data-testid={`${testId}-retry`} variant="outline" size="sm" className="text-[11px] gap-1.5" onClick={onRetry}>
          <ArrowClockwise size={13} /> Retry
        </Button>
      )}
    </div>
  );
}

export function StaleState({ lastRefreshed, onRefresh, testId = "stale-state" }) {
  return (
    <div data-testid={testId} className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-button)] border border-[var(--warning-border)] bg-[var(--warning-bg)] mb-3">
      <Clock size={14} className="text-[var(--warning)] shrink-0" />
      <p className="text-[10px] text-[var(--warning)] flex-1">
        {lastRefreshed ? `Showing data as of ${lastRefreshed}. Refresh for the latest state.` : "This data may be stale."}
      </p>
      {onRefresh && (
        <Button data-testid={`${testId}-refresh`} variant="ghost" size="sm" className="h-6 text-[10px] text-[var(--warning)] hover:text-[var(--warning)]" onClick={onRefresh}>
          Refresh
        </Button>
      )}
    </div>
  );
}

export function LoadingState({ testId = "loading-state", state = "thinking" }) {
  return (
    <div data-testid={testId} className="flex items-center justify-center py-12">
      <GLIMState state={state} size={56} showLabel={false} />
    </div>
  );
}
